<template>
  <div>
    <!-- 标题栏 + 发布按钮 -->
    <div class="header-row" :class="{ 'mobile-header': isMobile }">
      <h2>已发布的公告</h2>
      <el-button type="primary" @click="openCreateDialog">发布公告</el-button>
    </div>

    <div v-if="isOwner" class="ops-panel">
      <div class="ops-card">
        <div class="ops-title">维护模式</div>
        <div class="ops-control">
          <el-switch v-model="isMaintenance" />
          <el-button type="primary" @click="toggleMaintenance">
            {{ isMaintenance ? '关闭维护' : '开启维护' }}
          </el-button>
        </div>
      </div>
      <div class="ops-card">
        <div class="ops-title">发布新版本</div>
        <div class="version-form">
          <el-input v-model="newVersion" placeholder="请输入版本号" />
          <el-input v-model="downloadUrl" placeholder="下载URL，可选" />
          <el-button type="primary" @click="releaseNewVersion">发布版本</el-button>
        </div>
      </div>
    </div>

    <!-- 公告列表（原左侧区域） -->
    <el-container>
      <el-aside :class="{ 'mobile-full': isMobile }" width="100%">
        <el-table
          v-loading="announcementLoading"
          :data="announcements"
          empty-text="暂无公告"
          style="width: 100%"
          max-height="520"
        >
          <el-table-column prop="username" label="发布人昵称" width="120" />
          <el-table-column prop="pushtime" label="发布时间" width="180" />
          <el-table-column prop="info" label="内容" show-overflow-tooltip />
          <el-table-column prop="noticeid" label="公告ID" width="180" />
          <el-table-column prop="typeDesc" label="类型" width="100" />
          <el-table-column label="操作" width="150">
            <template #default="scope">
              <el-button @click="editAnnouncement(scope.row)" type="primary" size="small">修改</el-button>
              <el-button @click="deleteAnnouncement(scope.row.noticeid)" type="danger" size="small">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-aside>
    </el-container>

    <!-- 发布/编辑公告模态框 -->
    <el-dialog
      v-model="dialogVisible"
      :title="form.noticeid ? '修改公告' : '发布公告'"
      width="90%"
      :fullscreen="isMobile"
      :before-close="handleDialogClose"
    >
      <el-form :model="form" label-width="120px" label-position="top">
        <el-form-item label="发布人昵称" required>
          <el-input
            v-model="form.username"
            placeholder="如：Jack电玩、旭旭电玩（非管理员，不得写XX官方，一经查实永久封禁本功能使用）"
          />
        </el-form-item>
        <el-form-item label="内容" required>
          <el-input type="textarea" v-model="form.info" rows="4" />
        </el-form-item>
        <el-form-item label="发布对象" required>
          <el-select v-model="form.targetType" placeholder="请选择公告接收群体" @change="handleTargetTypeChange">
            <el-option label="普通用户（客户端显示）" value="user" />
            <el-option v-if="isOwner" label="经销商（后台显示）" value="dealer" />
          </el-select>
        </el-form-item>

        <el-form-item label="推送范围" required v-if="form.targetType === 'user'">
          <el-radio-group v-model="form.scopeType">
            <el-radio label="all" v-if="isOwner">全量推送（所有用户可见）</el-radio>
            <el-radio label="self">仅推给自己客户</el-radio>
            <el-radio label="specify" v-if="isOwner">推给指定经销商的客户</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item
          label="目标经销商"
          required
          v-if="form.targetType === 'user' && form.scopeType === 'specify'"
        >
          <el-select v-model="form.targetDealer" placeholder="请选择经销商" clearable>
            <el-option
              v-for="distributor in distributors"
              :key="distributor.username"
              :label="`${distributor.username} (${distributor.TaobaoShop})`"
              :value="distributor.username"
            ></el-option>
          </el-select>
        </el-form-item>

        <el-form-item label="推送范围" required v-else-if="form.targetType === 'dealer'">
          <el-radio-group v-model="form.scopeType">
            <el-radio label="all">全量推送（所有经销商可见）</el-radio>
            <el-radio label="specify" v-if="isOwner">推给指定经销商</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item
          label="目标经销商"
          required
          v-if="form.targetType === 'dealer' && form.scopeType === 'specify'"
        >
          <el-select v-model="form.targetDealer" placeholder="请选择经销商" clearable>
            <el-option
              v-for="distributor in distributors"
              :key="distributor.username"
              :label="`${distributor.username} (${distributor.TaobaoShop})`"
              :value="distributor.username"
            ></el-option>
          </el-select>
        </el-form-item>
      </el-form>

      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="submitForm">确认</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'
import axios from '../axios'
import { ElMessage } from '@/plugins/tdesignElementAdapter'

// 响应式数据
const announcements = ref([])
const announcementLoading = ref(false)
const isMobile = ref(window.innerWidth <= 768)
const dialogVisible = ref(false)
const isMaintenance = ref(false)
const newVersion = ref('')
const downloadUrl = ref('')

// 表单数据
const form = ref({
  username: '',
  info: '',
  targetType: 'user',
  scopeType: 'all',
  targetDealer: '',
  noticeid: ''
})

const distributors = ref([])

// Vuex store
const store = useStore()

// 计算属性：是否为超管（owner 为 0）
const isOwner = computed(() => {
  const owner = store.state.user?.owner
  return owner === 0 || owner === '0'
})

// 生命周期：组件挂载
onMounted(() => {
  fetchDistributors()
  fetchAnnouncements()
  if (isOwner.value) {
    getMaintenanceStatus()
  }
  window.addEventListener('resize', checkMobile)
})

// 生命周期：组件卸载前
onBeforeUnmount(() => {
  window.removeEventListener('resize', checkMobile)
})

// 方法
function checkMobile() {
  isMobile.value = window.innerWidth <= 768
}

function fetchAnnouncements() {
  const token = localStorage.getItem('user-token')
  announcementLoading.value = true
  axios.post('/index.php?action=getAnnouncements', { token })
    .then(response => {
      const data = response.data || {}
      if (data.success === false) {
        announcements.value = []
        ElMessage.error(data.message || '获取公告列表失败')
        return
      }

      const list = data.announcements || data.list || data.data?.list || data.data || []
      announcements.value = Array.isArray(list) ? list.map(normalizeAnnouncement) : []
    })
    .catch(error => {
      console.error('Error fetching announcements:', error)
      announcements.value = []
      ElMessage.error('获取公告列表失败')
    })
    .finally(() => {
      announcementLoading.value = false
    })
}

function normalizeAnnouncement(item) {
  const types = item.types ?? item.type
  let typeDesc = item.typeDesc || ''
  if (!typeDesc) {
    if (types == 0 || item.targetType === 'user') typeDesc = '用户公告'
    else if (types == 1 || item.targetType === 'dealer' || item.targetType === 'distributor') typeDesc = '经销商公告'
    else typeDesc = '公告'
  }

  return {
    ...item,
    username: item.username || item.title || '',
    pushtime: item.pushtime || item.createTime || item.create_time || '',
    info: item.info || item.content || '',
    noticeid: item.noticeid || item.id || '',
    types,
    typeDesc
  }
}

function fetchDistributors() {
  const token = localStorage.getItem('user-token')
  axios.post('/index.php?action=Distributors_list', { token })
    .then(response => {
      if (response.data.success) {
        distributors.value = response.data.distributors
      } else {
        ElMessage.error(response.data.message)
      }
    })
    .catch(error => {
      console.error('Error fetching distributors:', error)
    })
}

function getMaintenanceStatus() {
  const token = localStorage.getItem('user-token')
  axios.post('/index.php?action=getMaintenanceStatus', { token })
    .then(response => {
      isMaintenance.value = response.data.isMaintenance === true || response.data.isMaintenance === 1 || response.data.newStatus === 1
    })
    .catch(error => {
      console.error('Error fetching maintenance status:', error)
      ElMessage.error('获取维护状态失败')
    })
}

function toggleMaintenance() {
  const token = localStorage.getItem('user-token')
  axios.post('/index.php?action=toggleMaintenance', { token })
    .then(response => {
      if (response.data.success === false) {
        ElMessage.error(response.data.message || '维护状态更新失败')
        return
      }
      isMaintenance.value = response.data.newStatus === 1
      ElMessage.success(`已${isMaintenance.value ? '开启' : '关闭'}维护模式`)
    })
    .catch(error => {
      console.error('Error toggling maintenance:', error)
      ElMessage.error('维护状态更新失败')
    })
}

function releaseNewVersion() {
  const version = newVersion.value.trim()
  if (!version) {
    ElMessage.error('请输入版本号')
    return
  }

  const token = localStorage.getItem('user-token')
  axios.post('/index.php?action=releaseNewVersion', {
    token,
    version,
    download_url: downloadUrl.value.trim()
  })
    .then(response => {
      if (response.data.success === false) {
        ElMessage.error(response.data.message || '新版本发布失败')
        return
      }
      ElMessage.success('新版本发布成功')
      newVersion.value = ''
      downloadUrl.value = ''
    })
    .catch(error => {
      console.error('Error releasing version:', error)
      ElMessage.error('新版本发布失败')
    })
}

function handleTargetTypeChange() {
  form.value.scopeType = 'all'
  form.value.targetDealer = ''
}

function openCreateDialog() {
  resetForm()
  dialogVisible.value = true
}

function editAnnouncement(announcement) {
  form.value = {
    username: announcement.username,
    info: announcement.info,
    targetType: announcement.types == 0 ? 'user' : 'dealer',
    scopeType: announcement.status == 0 ? 'all' : (announcement.publisher ? 'self' : 'all'),
    targetDealer: announcement.publisher || '',
    noticeid: announcement.noticeid
  }
  // 修正 scopeType（指定经销商的情况）
  if (announcement.status == 1 && announcement.publisher && isOwner.value) {
    form.value.scopeType = 'specify'
  }
  dialogVisible.value = true
}

function deleteAnnouncement(noticeid) {
  const token = localStorage.getItem('user-token')
  axios.post('/index.php?action=deleteAnnouncement', { token, noticeid })
    .then(response => {
      if (response.data.success) {
        ElMessage.success('公告删除成功')
        fetchAnnouncements()
      } else {
        ElMessage.error(response.data.message)
      }
    })
    .catch(error => {
      console.error('Error deleting announcement:', error)
      ElMessage.error('删除失败，请稍后重试')
    })
}

function submitForm() {
  const submitData = {
    token: localStorage.getItem('user-token'),
    username: form.value.username,
    info: form.value.info,
    targetType: form.value.targetType,
    scopeType: form.value.scopeType,
    targetDealer: form.value.targetDealer,
    noticeid: form.value.noticeid,
  }

  const url = form.value.noticeid ? '/index.php?action=updateAnnouncement' : '/index.php?action=addAnnouncement'
  axios.post(url, submitData)
    .then(response => {
      if (response.data.success) {
        ElMessage.success(form.value.noticeid ? '公告更新成功' : '公告发布成功')
        dialogVisible.value = false
        resetForm()
        fetchAnnouncements()
      } else {
        ElMessage.error(response.data.message)
      }
    })
    .catch(error => {
      console.error('Error submitting form:', error)
      ElMessage.error('提交失败，请稍后重试')
    })
}

function resetForm() {
  form.value = {
    username: '',
    info: '',
    targetType: 'user',
    scopeType: 'all',
    targetDealer: '',
    noticeid: ''
  }
}

function handleDialogClose(done) {
  resetForm()
  done()
}
</script>

<style scoped>
.header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 40px 30px 40px 30px;
}

.mobile-header {
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
}

.ops-panel {
  display: grid;
  grid-template-columns: minmax(260px, 360px) 1fr;
  gap: 16px;
  padding: 0 30px 20px;
}

.ops-card {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 16px;
  background: #fff;
}

.ops-title {
  margin-bottom: 12px;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.ops-control,
.version-form {
  display: flex;
  align-items: center;
  gap: 12px;
}

.version-form .el-input {
  max-width: 260px;
}

.el-container {
  padding: 0 20px 20px 20px;
  display: flex;
  flex-direction: row;
}

.mobile-full {
  width: 100%;
  margin-bottom: 20px;
}

h2 {
  margin: 0;
}

.el-aside {
  padding-right: 20px;
}

.el-table {
  margin-bottom: 20px;
}

@media (max-width: 768px) {
  .el-container {
    flex-direction: column;
  }
  .el-aside {
    padding-right: 0;
  }
  .ops-panel {
    grid-template-columns: 1fr;
    padding: 0 20px 20px;
  }
  .ops-control,
  .version-form {
    align-items: stretch;
    flex-direction: column;
  }
  .version-form .el-input {
    max-width: none;
  }
}
</style>
